import { catchError, Observable, throwError } from 'rxjs';
import { ResultSet } from '../models/result-set.model';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Base } from '../models/base.model';
import { ToastrService } from 'ngx-toastr';
import { ErrorHandlerService } from './error-handler.service';

export abstract class ApiService<T extends Base> {
  
  abstract readonly _baseUrl: string;

  abstract readonly searchFields: string[];

  constructor(protected http: HttpClient, protected errorHandler: ErrorHandlerService) { }  

  protected constructSearchQueryString(search: string): HttpParams {
    let params = new HttpParams();
  
    this.searchFields.forEach((field, index) => {
      params = params
        .append(`search[${index}][field]`, field)
        .append(`search[${index}][query]`, search);
    });
  
    return params;
  }

  protected handleError = (error: HttpErrorResponse) => {
      return this.errorHandler.handle(error);
  }

  getAll(offset: number = 0, limit: number=10) {
    const params = new HttpParams()
      .set('offset', offset.toString())
      .set('limit', limit.toString());

    return this.http.get<ResultSet<T>>(this._baseUrl, { params }).pipe(
      catchError(this.handleError)
    );
  }

  get(id: number) {
    return this.http.get<T>(`${this._baseUrl}${id}`).pipe(
      catchError(this.handleError)
    );
  }

  search(search: string, offset: number = 0, limit: number=10): Observable<ResultSet<T>> {
    let params = this.constructSearchQueryString(search);
    params = params.set('offset', offset.toString()).set('limit', limit.toString());   
    return this.http.get<ResultSet<T>>(`${this._baseUrl}search?`, { params }).pipe(
      catchError(this.handleError)
    );
  }

  create(model: T) {
    return this.http.post<T>(this._baseUrl, model).pipe(
      catchError(this.handleError)
    );
  }

  update(model: T) {
    return this.http.put<T>(`${this._baseUrl}${model.id}`, model).pipe(
      catchError(this.handleError)
    );
  }

  delete(modelID: number) {
    return this.http.delete(`${this._baseUrl}${modelID}`).pipe(
      catchError(this.handleError)
    );
  }

  downloadCSV() {
    return this.http.get(`${this._baseUrl}export`, 
    {
      responseType: 'blob', // Important to receive binary data
      observe: 'response'   // To access headers
    })
    .pipe(
      catchError(this.handleError)
    );
  }
}
